import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Loader2, Minus, Star } from 'lucide-react';
import { getSalesActivityMetricItems } from '../../services/api/implementation/impl';

const trendIcon = (trend) => {
  if (trend === 'up') {
    return <ArrowUp className="h-4 w-4 shrink-0 stroke-[2.5] text-emerald-600" aria-label="up" />;
  }
  if (trend === 'down') {
    return <ArrowDown className="h-4 w-4 shrink-0 stroke-[2.5] text-rose-600" aria-label="down" />;
  }
  return <Minus className="h-4 w-4 shrink-0 stroke-[2.5] text-gray-400" aria-label="flat" />;
};

/** Signed period-over-prior delta (e.g. +5 / -2 / 0). */
const periodDelta = (row) => {
  const current = Number(row?.periodCount ?? row?.total ?? 0) || 0;
  const previous = Number(row?.previousPeriodCount ?? 0) || 0;
  return current - previous;
};

const formatDelta = (delta) => {
  if (delta > 0) return `+${delta}`;
  if (delta < 0) return `${delta}`;
  return '0';
};

const deltaClass = (delta) => {
  if (delta > 0) return 'text-emerald-600';
  if (delta < 0) return 'text-rose-600';
  return 'text-gray-400';
};

const MetricCounts = ({ row }) => {
  if (row?.kind === 'pre_leads') {
    const total = Number(row.total ?? row.periodCount ?? 0) || 0;
    const qualified = Number(row.qualified ?? 0) || 0;
    const unqualified = Number(row.unqualified ?? 0) || 0;
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 font-mono text-xs font-semibold tabular-nums">
        <span className="text-gray-800">{total}</span>
        <span
          className="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-bold leading-none bg-emerald-50 text-emerald-700"
          title="Qualified">
          Q {qualified}
        </span>
        <span
          className="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-bold leading-none bg-rose-50 text-rose-700"
          title="Unqualified">
          UQ {unqualified}
        </span>
      </span>
    );
  }

  return (
    <span className="shrink-0 font-mono text-xs font-semibold tabular-nums text-gray-800">
      {Number(row?.periodCount ?? 0) || 0}
      <span className="text-gray-400"> / </span>
      {Number(row?.totalCount ?? 0) || 0}
    </span>
  );
};

const MetricRowDetails = ({
  projectId,
  metricKey,
  salesUserId,
  preset,
  from,
  to
}) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'salesActivityMetricItems',
      projectId,
      metricKey,
      salesUserId || 'all',
      preset,
      from || '',
      to || ''
    ],
    queryFn: () =>
      getSalesActivityMetricItems(projectId, metricKey, {
        salesUserId: salesUserId || undefined,
        preset,
        from: from || undefined,
        to: to || undefined
      }),
    enabled: !!projectId && !!metricKey,
    staleTime: 60_000
  });

  const items = data?.items || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (isError) {
    return (
      <p className="px-2 py-3 text-xs text-rose-600">
        {error?.message || 'Failed to load details'}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="px-2 py-3 text-center text-xs text-gray-500">
        Nothing moved in this period.
      </p>
    );
  }

  return (
    <ul className="max-h-44 space-y-1.5 overflow-y-auto pr-1">
      {items.map((item) => (
        <li
          key={item.id}
          className="rounded-md border border-gray-100 bg-white px-2.5 py-1.5 text-left">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">
                {item.primary}
              </p>
              <p className="truncate text-xs text-gray-600">
                {item.title}
                {item.secondary ? ` · ${item.secondary}` : ''}
              </p>
            </div>
            <span className="shrink-0 text-[11px] font-medium text-gray-500">
              {item.displayTime}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

/**
 * Expandable stage / pre-lead / stagnant metric rows for Action Center Activity.
 */
const SalesUserActivityMetrics = ({
  metrics = [],
  isLoading = false,
  projectId,
  salesUserId = '',
  preset = 'today',
  from = '',
  to = ''
}) => {
  const [expandedKey, setExpandedKey] = useState(null);

  if (isLoading && (!metrics || metrics.length === 0)) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading metrics…
      </div>
    );
  }

  if (!metrics?.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-500">
        No activity metrics for this filter.
      </div>
    );
  }

  return (
    <div className="max-h-[min(28rem,55vh)] shrink-0 space-y-1 overflow-y-auto pr-0.5">
      {metrics.map((row) => {
        const open = expandedKey === row.key;
        const delta = periodDelta(row);
        return (
          <div
            key={row.key}
            className={`rounded-lg border ${
              open ? 'border-[#346C75] bg-teal-50/40' : 'border-gray-200 bg-white'
            }`}>
            <button
              type="button"
              onClick={() => setExpandedKey(open ? null : row.key)}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
              {open ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-600" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
              )}
              <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
              <span
                className={`shrink-0 font-mono text-xs font-bold tabular-nums ${deltaClass(delta)}`}
                title="Change vs prior period">
                {formatDelta(delta)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-900">
                {row.label}
              </span>
              <MetricCounts row={row} />
              {trendIcon(row.trend)}
            </button>
            {open && (
              <div className="border-t border-gray-100 px-2 pb-2 pt-1.5">
                <MetricRowDetails
                  projectId={projectId}
                  metricKey={row.key}
                  salesUserId={salesUserId}
                  preset={preset}
                  from={from}
                  to={to}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SalesUserActivityMetrics;
